import React, { useEffect, useRef } from 'react';
import { BrowserRouter as Router, Redirect, Route, Switch, useLocation } from 'react-router-dom';
import { ContextProvider, EntryStateProvider, AuthContext, useGeneralContext, useAuthContext } from './context';
import SingleEntry from './views/entry/SingleEntry';
import LandingPage from './LandingPage';
import { PageNotFound } from './NotFound';
import Home from './views/home/Home';
import Settings from './views/settings/Settings';
import Fab from './components/floating-action-button/floating-action-button';
import Faq from './views/faq/Faq';
import AboutUs from './views/about-us/AboutUs';
import Retrospective from './views/retrospective/Retrospective';
import { Toaster } from 'react-hot-toast';
import FeedbackModal from './views/feedback-modal/FeedbackModal';
import ChoosePlan from './views/choose-plan/ChoosePlan';
import TermsAndPrivacy from './views/terms-and-privacy/TermsAndPrivacy';
import { Amplify, Auth } from 'aws-amplify';
import awsExports from './aws-exports';
import { Authenticator, useAuthenticator } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import components from './amplify-customization';
import { Api, retrieveSubscription, getUserData } from './api';
import { COUNTRY_CURRENCY_MAP, isSupportedCountry } from './views/choose-plan/data';
import { getReferralCookie } from './util/cookies';
import { clearCognitoStorage, hasStoredCognitoSession } from './util/authStorage';
import ErrorBoundary from './components/ErrorBoundary';
import FullPageSpinner from './components/FullPageSpinner';

Amplify.configure({
  Auth: {
    region: awsExports.REGION,
    userPoolId: awsExports.USER_POOL_ID,
    userPoolWebClientId: awsExports.USER_POOL_APP_CLIENT_ID
  },
  oauth: {
    domain: 'auth.jumblejournal.org', // Replace with your Cognito domain
    scope: ['email', 'profile', 'openid'],
    redirectSignIn:
      process.env.NODE_ENV === 'development' ? 'http://localhost:3000/home' : 'https://jumblejournal.org/home', // Replace with your redirect URL
    redirectSignOut: process.env.NODE_ENV === 'development' ? 'http://localhost:3000' : 'https://jumblejournal.org',
    responseType: 'code' // or 'token', depending on your use case
  }
});

// TODO updgrade to v6 https://reactrouter.com/en/main/upgrading/v5
function App() {
  return (
    <Router>
      <div className="tw-grid tw-grid-cols-12 tw-min-h-screen tw-bg-white tw-font-sans tw-text-base">
        <ErrorBoundary>
          <Authenticator.Provider>
            <ContextProvider>
              <EntryStateProvider>
                <Toaster />
                <FeedbackModal />
                <ChoosePlan />
                <Switch>
                  <Route path="/terms-and-privacy" component={TermsAndPrivacy} />
                  <ProtectedRoute path="/settings" component={Settings} />
                  <Route path="/faq" component={Faq} />
                  <Route path="/about" component={AboutUs} />
                  {/* Chat with Journal is now part of the Explore your Journal
                      dashboard; a bookmarked /chat link still lands there,
                      carrying over any state (e.g. a question asked from Home). */}
                  <Route path="/chat" component={ChatRedirect} />
                  <ProtectedRoute exact path="/entry/:entryDateTitle" component={SingleEntry} />
                  <ProtectedRoute exact path="/entry" component={SingleEntry} />
                  <ProtectedRoute exact path="/journal_with_:persona" component={SingleEntry} />
                  <ProtectedRoute exact path="/retrospective" component={Retrospective} />
                  <ProtectedRoute path="/home" component={Home} />
                  <Route exact path="/" component={LandingPage} />
                  <Route path="*" component={PageNotFound} />
                </Switch>
              </EntryStateProvider>
              <Fab />
            </ContextProvider>
          </Authenticator.Provider>
        </ErrorBoundary>
      </div>
    </Router>
  );
}

const ChatRedirect = () => {
  const location = useLocation<{ initialQuery?: string } | undefined>();
  return <Redirect to={{ pathname: '/retrospective', state: location.state }} />;
};

/**
 * Routes Amplify renders nothing for. `setup` is the slow one: it covers the
 * round trip to Cognito that revalidates the stored session, which takes
 * several seconds whenever that session is no longer refreshable.
 */
const BLANK_AUTH_ROUTES = ['idle', 'setup', 'transition'];

const ProtectedRoute = (props: { component: any; path: string; exact?: true }) => {
  const { component: Component, path } = props;
  const exact = props.exact || false;
  const { authStatus, route } = useAuthenticator((context) => [context.authStatus, context.route]);

  // A stored session that Cognito refuses stays in localStorage and costs the
  // same failed round trip on every load. Drop it the first time it is
  // rejected so the next visit goes straight to the sign-in form.
  const wasConfiguring = useRef(authStatus === 'configuring');
  useEffect(() => {
    if (authStatus === 'configuring') {
      wasConfiguring.current = true;
      return;
    }
    if (authStatus === 'unauthenticated' && wasConfiguring.current && hasStoredCognitoSession()) {
      clearCognitoStorage();
    }
    wasConfiguring.current = false;
  }, [authStatus]);

  useEffect(() => {
    const refreshTokens = async () => {
      try {
        await Auth.currentSession(); // This will refresh the token if it's about to expire
      } catch (error) {
        // Handle any errors that occur during token refresh
        console.log('Error refreshing token:', error);
      }
    };

    const interval = setInterval(refreshTokens, 1000 * 60 * 10); // Refresh the token every 10 minutes

    return () => clearInterval(interval); // Clean up the interval when the component unmounts
  }, []);

  const referralCode = getReferralCookie();

  return (
    <>
      {/* The Authenticator stays mounted through this — it is what drives the
          state machine — but it renders nothing on these routes, so without a
          spinner the page is blank white for as long as the handshake takes. */}
      {BLANK_AUTH_ROUTES.includes(route) && <FullPageSpinner label="Checking your session" />}
      <Authenticator
        variation="modal"
        loginMechanisms={['email']}
        socialProviders={['google']}
        signUpAttributes={['nickname', 'custom:referral_code' as any]}
        formFields={{
          signUp: {
            'custom:referral_code': {
              label: 'Referral Code (readonly)',
              order: 3,
              placeholder: '',
              defaultValue: referralCode || '',
              isReadOnly: true
            }
          }
        }}
        components={components}
      >
        {({ signOut, user }) => {
          // Amplify calls this on the way out of an authenticated session too,
          // when both values are already gone. Rendering the app then throws in
          // useAuthContext and takes the whole tree down, so wait it out.
          if (!user || !signOut) {
            return <FullPageSpinner label="Checking your session" />;
          }

          return (
            <AuthContext.Provider value={{ signOut, user }}>
              <AppRoute exact={exact} path={path} component={Component} />
            </AuthContext.Provider>
          );
        }}
      </Authenticator>
    </>
  );
};

const AppRoute = (props: { component: any; path: string; exact: boolean }) => {
  const { component: Component, path } = props;
  const exact = props.exact || false;

  const context = useGeneralContext();
  const {
    subscriptionStatus,
    setSubscriptionStatus,
    setCurrency,
    setJwtIsSet,
    jwtIsSet,
    setEntryBackgroundColor,
    setNickname,
    setImportationEmail,
    setSubscribed,
    setSubscribedToExtractsEmail,
    setReferralCode
  } = context;
  const authContext = useAuthContext();
  const { user } = authContext;

  useEffect(() => {
    // retreive use settings
    const retrieveUserSettings = async () => {
      try {
        const { settings, importationEmail, referralCode } = await getUserData();

        if (settings?.entryBackgroundColor) {
          setEntryBackgroundColor(settings?.entryBackgroundColor);
        }
        if (settings?.nickname) {
          setNickname(settings?.nickname);
        }

        if (settings?.subscribedToExtractsEmail) {
          setSubscribedToExtractsEmail(settings?.subscribedToExtractsEmail);
        }
        if (settings.octopusUserId && settings.octopusStatus === 'SUBSCRIBED') {
          setSubscribed(true);
        }
        if (importationEmail) {
          setImportationEmail(importationEmail);
        }
        if (referralCode) {
          setReferralCode(referralCode);
        }
      } catch (error) {}
    };
    if (!jwtIsSet) {
      return;
    }
    retrieveUserSettings();
  }, [
    setEntryBackgroundColor,
    setNickname,
    jwtIsSet,
    setImportationEmail,
    setSubscribed,
    setSubscribedToExtractsEmail,
    setReferralCode
  ]);

  useEffect(() => {
    if (!user) {
      return;
    }
    user.getSession((error: any, session: any) => {
      if (error) {
        return;
      }
      const jwt: string = session.getIdToken().getJwtToken();
      if (!jwt) {
        return;
      }
      Api.defaults.headers.common['Authorization'] = jwt;
      setJwtIsSet(true);
    });
  }, [user, setJwtIsSet]);

  useEffect(() => {
    const getGeoLocation = async () => {
      try {
        // Fetch the GeoJS geolocation data
        const response = await fetch('https://get.geojs.io/v1/ip/geo.json');
        const data = await response.json();
        const countryCode = data && data.country_code;
        if (isSupportedCountry(countryCode)) {
          setCurrency(COUNTRY_CURRENCY_MAP[countryCode]);
        }
      } catch (error) {
        console.log('unable to determine user location');
      }
    };
    getGeoLocation();
  }, [setCurrency]);

  useEffect(() => {
    const setUserSubscriptionStatus = async () => {
      try {
        const userSub = await retrieveSubscription();
        if (userSub?.data?.stripeCustomerId) {
          sessionStorage.setItem('stripeCustomerId', userSub?.data?.stripeCustomerId);
        }
        setSubscriptionStatus(userSub?.data?.status || null);
      } catch (error) {
        console.error(error);
      }
    };
    if (!jwtIsSet) {
      return;
    }
    setUserSubscriptionStatus();
  }, [subscriptionStatus, user, setSubscriptionStatus, jwtIsSet]);

  return <Route exact={exact} path={path} component={Component} />;
};

export default App;
